import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Zap, Phone, ShieldCheck, AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import ConfirmationView from "../components/enroll/ConfirmationView";
import { trackLead } from "../lib/ga";

const STORAGE_KEY = "velcor_enroll_complete_data";

/**
 * EnrollmentComplete — dedicated thank-you / conversion page.
 *
 * Why a separate URL?
 * Google Ads (and most ad networks: Meta, TikTok, GA4 destination goals)
 * fire conversion events when the browser lands on a specific URL. We can't
 * trigger that off an in-page state change, so we land here at
 * `/enrollment-complete` after a successful enrollment.
 *
 * Data flow:
 * Enroll.jsx writes the order payload to sessionStorage and navigate()s
 * here. This component reads it on mount and renders the SAME
 * `ConfirmationView` the legacy in-page flow used to render — so visually
 * the user sees zero difference.
 *
 * If a user lands here directly (e.g., bookmark, reload after clearing
 * session storage), we bounce them back to `/enroll` so they never see
 * an empty thank-you page.
 */
export default function EnrollmentComplete() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let raw = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      raw = null;
    }
    if (!raw) {
      setMissing(true);
      return;
    }
    try {
      setPayload(JSON.parse(raw));
    } catch (e) {
      setMissing(true);
    }
  }, []);

  // Fire Google Ads + GA4 conversion on mount, BEFORE any payload check or
  // redirect logic. Tag Assistant probes this URL directly without a real
  // session, so we must dispatch the conversion event on every landing — the
  // event itself is what Google Ads listens for. Details (orderNumber, plan)
  // are optional enrichments; they get filled in later via the payload effect
  // below if present. Empty-payload landings (rare; bookmarks/refreshes) still
  // emit the conversion before redirecting to /enroll, which is acceptable —
  // visits to /enrollment-complete from the SPA flow are inherently lead
  // signals, and bookmark visits are negligible volume.
  useEffect(() => {
    trackLead({ orderNumber: "", plan: "", value: 35 });
  }, []);

  // Re-fire WITH order details once the payload is available, so analytics
  // can attribute the lead to the specific plan/order in GA4 reports.
  useEffect(() => {
    if (!payload) return;
    trackLead({
      orderNumber: payload.orderNumber || "",
      plan: payload.currentPlan?.name || payload.currentPlan?.id || "",
      value: 35,
    });
  }, [payload]);

  // Direct-hit / refresh after clearing storage → back to enroll.
  if (missing) return <Navigate to="/enroll" replace />;
  if (!payload) return null;

  const {
    orderNumber = "",
    form = {},
    currentPlan = null,
    estimate = null,
    address = "",
    unit = "",
    city = "",
    zip = "",
    esiidParam = "",
    utilityParam = "",
    deposit = null,
  } = payload;

  const depositRequired = !!(deposit && deposit.required && Number(deposit.amount_usd) > 0);

  return (
    <Layout minimal>
      <Helmet>
        <title>¡Gracias! Activación enviada — Velcor Energy</title>
        <meta name="description" content="Tu solicitud de activación fue enviada con éxito. Un asesor de Velcor Energy te contactará en 30 minutos." />
        {/* Don't index the thank-you page — it's transactional, not a landing
            page for new traffic. Keeps it eligible as a conversion URL while
            keeping it out of organic search. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="fixed inset-0 -z-10 bg-white" aria-hidden />

      {/* Slim brand bar (same minimal style as Enroll.jsx) */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1180px] mx-auto px-5 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 group"
            data-testid="enroll-complete-brand"
          >
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-velcor-green text-white shadow-[0_0_18px_rgba(47,122,58,0.4)] group-hover:scale-105 transition-transform">
              <Zap className="w-4 h-4" strokeWidth={2.6} />
            </span>
            <span className="font-black text-[14.5px] tracking-tight text-velcor-ink">VELCOR ENERGY</span>
          </button>
          <a
            href="tel:+18007156516"
            className="hidden sm:inline-flex items-center gap-1.5 text-[12px] font-extrabold text-slate-600 hover:text-velcor-green transition-colors tabular-nums"
            data-testid="enroll-complete-phone"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.4} />
            800-715-6516
          </a>
        </div>
      </header>

      <section
        className="relative max-w-[1180px] mx-auto px-5 pt-8 md:pt-10 pb-24 bg-white"
        data-testid="enrollment-complete-page"
      >
        {depositRequired && (
          <DepositRequiredBanner deposit={deposit} orderNumber={orderNumber} />
        )}
        <ConfirmationView
          orderNumber={orderNumber}
          form={form}
          currentPlan={currentPlan}
          estimate={estimate}
          address={address}
          unit={unit}
          city={city}
          zip={zip}
          esiidParam={esiidParam}
          utilityParam={utilityParam}
        />
      </section>
    </Layout>
  );
}

function DepositRequiredBanner({ deposit, orderNumber }) {
  const [redirecting, setRedirecting] = useState(false);
  const amount = Number(deposit?.amount_usd || 0);
  const url = deposit?.checkout_url;

  const handlePay = () => {
    if (!url) return;
    setRedirecting(true);
    // Replace so the back button doesn't trap them between confirmation
    // and Stripe. They can come back via /portal once activated.
    window.location.assign(url);
  };

  return (
    <div
      className="mb-8 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden"
      data-testid="enroll-deposit-banner"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start gap-3 mb-4">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-amber-500 text-white shrink-0 mt-0.5">
            <AlertCircle className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.16em] text-amber-700 mb-1">
              Acción requerida · Depósito de garantía
            </div>
            <h2 className="text-[18px] md:text-[20px] font-black text-velcor-ink leading-tight">
              Para completar tu activación necesitamos un depósito de
              ${amount} <span className="text-amber-700">(reembolsable)</span>
            </h2>
          </div>
        </div>

        <div className="bg-white border border-amber-200 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" strokeWidth={2.4} />
            <div className="text-[13.5px] leading-relaxed text-slate-700">
              <strong className="text-velcor-ink">Tu dinero está protegido.</strong>{" "}
              Velcor te devuelve el depósito completo (con interés) tras
              <strong> 12 meses</strong> de pago a tiempo — según regla
              PUCT §25.478 que protege a clientes de Texas. Si decides
              cancelar antes, también lo recibes de vuelta.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handlePay}
          disabled={!url || redirecting}
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-velcor-green hover:bg-velcor-green/90 text-white text-[14.5px] font-extrabold shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-60"
          data-testid="enroll-deposit-pay-btn"
        >
          {redirecting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Redirigiendo a pago seguro…
            </>
          ) : (
            <>
              Pagar ${amount} ahora con tarjeta
              <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
            </>
          )}
        </button>

        <div className="mt-3 text-[11.5px] text-slate-500 leading-snug">
          También te enviamos este link por correo a tu email registrado.
          Puedes pagar después si lo prefieres — tu activación queda en pausa
          hasta entonces. Orden{" "}
          <span className="font-mono font-bold text-slate-700">{orderNumber}</span>.
        </div>
      </div>
    </div>
  );
}

// Export the storage key so Enroll.jsx writes to the exact same slot.
export const ENROLL_COMPLETE_STORAGE_KEY = STORAGE_KEY;
